import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Funnel, PaperPlaneRight, Paperclip, X } from "@phosphor-icons/react";
import { DocumentAttachmentList } from "@/components/DocumentAttachment";

export default function Broadcast() {
  const [campaignName, setCampaignName] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [filterCustomerType, setFilterCustomerType] = useState("all");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const broadcastMessageRef = useRef(broadcastMessage);
  useEffect(() => { broadcastMessageRef.current = broadcastMessage; }, [broadcastMessage]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const tRes = await api.get("/templates");
        const broadcastTemplate = tRes.data.find(t => t.category === "service" && t.stage === "service_broadcast" && t.type === "whatsapp");
        if (broadcastTemplate && !broadcastMessageRef.current) {
          setBroadcastMessage(broadcastTemplate.body);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchTemplates();
  }, []);

  const handleBroadcast = async () => {
    if (!campaignName) return toast.error("Please enter a campaign name");
    if (!broadcastMessage) return toast.error("Please enter a message");

    setSending(true);
    try {
      await api.post("/service/broadcast", {
        offer_message: broadcastMessage,
        subject: campaignName,
        audience: filterCustomerType,
        attachments: attachments.length > 0 ? attachments : null
      });
      toast.success("Broadcast campaign started successfully!");
      setBroadcastMessage("");
      setCampaignName("");
      setAttachments([]);
    } catch (e) {
      toast.error("Failed to start broadcast");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        const res = await api.post("/upload", fd);
        if (res.data?.url) {
          setAttachments(prev => [...prev, res.data.url]);
          toast.success(`Attached "${files[0].name}"`);
        }
      } else {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        const res = await api.post("/upload/multiple", fd);
        const newUrls = (res.data?.files || []).map(f => f.url);
        if (newUrls.length > 0) {
          setAttachments(prev => [...prev, ...newUrls]);
          toast.success(`${newUrls.length} files attached successfully`);
        }
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="broadcast-page">
      <div className="mb-8">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B7280] mb-2">Marketing</div>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Bulk Service Offer Broadcast</h1>
      </div>

      <div className="space-y-8">
        <div className="bg-white border border-[#E5E7EB] p-6 shadow-sm relative">
          <div className="absolute -left-3 -top-3 w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center font-bold text-sm">1</div>
          <div className="flex items-center gap-2 mb-4 ml-2">
            <Megaphone size={24} className="text-[#002FA7]" />
            <h2 className="font-heading font-bold text-xl">Create Service Offer Campaign</h2>
          </div>
          <div className="space-y-4 ml-2">
            <div>
              <label className="text-sm font-bold text-gray-700 block mb-1">Campaign Name</label>
              <input
                value={campaignName}
                onChange={e => setCampaignName(e.target.value)}
                placeholder="e.g. Summer Tune-up 10% Off"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-gray-700 block mb-1">Offer Notification Message</label>
              <textarea
                value={broadcastMessage}
                onChange={e => setBroadcastMessage(e.target.value)}
                placeholder="e.g. Get 10% off our seasonal preventive maintenance if you book this week! Reply to this message to claim."
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none resize-none h-32 mb-2"
              />
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-[#002FA7] font-bold text-sm hover:underline">
                    <Paperclip size={16} weight="bold" />
                    <span>{uploading ? "Uploading..." : "Attach Documents, Images & Videos"}</span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,application/pdf,image/*,video/*,.mp4,.mov,.webm,.3gp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>
                  {attachments.length > 0 && (
                    <span className="text-xs font-bold text-[#10B981]">{attachments.length} attached</span>
                  )}
                </div>
                {attachments.length > 0 && (
                  <div className="mt-2">
                    <DocumentAttachmentList
                      attachments={attachments}
                      onRemove={(idx) => setAttachments(attachments.filter((_, i) => i !== idx))}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-6 shadow-sm relative">
          <div className="absolute -left-3 -top-3 w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center font-bold text-sm">2</div>
          <div className="flex items-center gap-2 mb-4 ml-2">
            <Funnel size={24} className="text-[#002FA7]" />
            <h2 className="font-heading font-bold text-xl">Filter All Relevant Customers (Opted-in)</h2>
          </div>
          <div className="ml-2">
            <p className="text-sm text-[#6B7280] mb-4">Select the audience for this campaign. By default, it targets opted-in customers who previously declined AMC/Service.</p>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="audience" checked={filterCustomerType === 'all'} onChange={() => setFilterCustomerType('all')} />
                <span className="text-sm font-medium">All Eligible (Chargeable Leads)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="audience" checked={filterCustomerType === 'expired_amc'} onChange={() => setFilterCustomerType('expired_amc')} />
                <span className="text-sm font-medium">Expired AMCs Only</span>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-6 shadow-sm relative">
          <div className="absolute -left-3 -top-3 w-8 h-8 bg-[#10B981] text-white flex items-center justify-center font-bold text-sm">3</div>
          <div className="flex items-center gap-2 mb-4 ml-2">
            <PaperPlaneRight size={24} className="text-[#10B981]" />
            <h2 className="font-heading font-bold text-xl">Send Bulk Offer Notifications</h2>
          </div>
          <div className="ml-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-sm text-[#6B7280]">Ready to send your <strong>"{campaignName || "Unnamed Campaign"}"</strong> offer to the filtered audience.</p>
            <button
              onClick={handleBroadcast}
              disabled={sending || !broadcastMessage || !campaignName}
              className="bg-[#10B981] text-white px-8 py-3 font-bold hover:bg-[#059669] disabled:opacity-50 transition-colors flex items-center justify-center gap-2 min-w-[200px]"
            >
              {sending ? "Sending..." : "Launch Campaign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
